import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { fmtTRY } from "@/lib/format";

/**
 * Son N ayın kâr trendi kartı (Dashboard).
 * GET /api/reports/profit-trend?months=6 → [{month, sale, cost, profit, orders}]
 * SVG bar chart: satış (gri bar, arka plan) + kâr (renkli overlay).
 */
export default function ProfitTrendCard({ months = 6 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/profit-trend?months=${months}`)
      .then((r) => setRows(r.data?.months || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [months]);

  const totals = useMemo(() => {
    let sale = 0, cost = 0, profit = 0, orders = 0;
    rows.forEach((r) => { sale += r.sale; cost += r.cost; profit += r.profit; orders += r.orders; });
    // Kârlılık: maliyet üzeri markup — Fiyat Hesaplama / Sipariş ekranlarıyla tutarlı.
    const marginPct = cost > 0 ? (profit / cost) * 100 : 0;
    return { sale, cost, profit, orders, marginPct };
  }, [rows]);

  const maxAbs = useMemo(() => {
    return Math.max(1, ...rows.map((r) => Math.max(Math.abs(r.sale), Math.abs(r.profit))));
  }, [rows]);

  const monthLabel = (k) => {
    const [y, m] = (k || "").split("-");
    const names = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
    const idx = parseInt(m, 10) - 1;
    return `${names[idx] || m} ${(y || "").slice(2)}`;
  };

  return (
    <div className="brutal-card p-5" data-testid="profit-trend-card">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-caps mb-1 flex items-center gap-1.5">
            <TrendingUp size={12} /> Son {months} Ay Kâr Trendi
          </div>
          <div className="text-[11px] text-neutral-500">
            Tedarikçi siparişleri (iptal hariç) + eşleşen tekliflerden hesaplanır
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Dönem Kârı</div>
          <div className={`font-mono-num text-xl font-bold ${
            totals.profit > 0 ? "text-emerald-700" : totals.profit < 0 ? "text-rose-700" : ""
          }`} data-testid="profit-trend-total">
            {totals.profit >= 0 ? "+" : ""}{fmtTRY(totals.profit)}
          </div>
          <div className="text-[10px] text-neutral-500 font-mono-num">
            {totals.orders} sipariş · Marj %{totals.marginPct.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} <span className="text-neutral-400">(maliyet üzeri)</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-neutral-500 text-sm">Yükleniyor…</div>
      ) : rows.length === 0 || totals.orders === 0 ? (
        <div className="text-center py-10 text-neutral-400 text-sm">
          Henüz sipariş yok — Fiyat Teklifi → Olumlu → Tedarikçiye Sipariş açtığınızda burası dolar.
        </div>
      ) : (
        <>
          <div className="flex items-end gap-2 h-40" data-testid="profit-trend-chart">
            {rows.map((r, i) => {
              const salePct = (Math.abs(r.sale) / maxAbs) * 100;
              const profitPct = (Math.abs(r.profit) / maxAbs) * 100;
              const positive = r.profit >= 0;
              const Icon = positive ? TrendingUp : r.profit === 0 ? Minus : TrendingDown;
              return (
                <div key={r.month} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={
                  `${monthLabel(r.month)}\nSatış: ${fmtTRY(r.sale)}\nMaliyet: ${fmtTRY(r.cost)}\nKâr: ${fmtTRY(r.profit)}\n${r.orders} sipariş`
                }>
                  <div className={`text-[10px] font-mono-num font-semibold ${
                    positive ? "text-emerald-700" : r.profit < 0 ? "text-rose-700" : "text-neutral-400"
                  }`}>
                    <Icon size={9} className="inline -mt-0.5" /> {r.profit === 0 ? "—" : fmtShort(r.profit)}
                  </div>
                  <div className="flex-1 w-full relative border border-black/10 bg-neutral-50 flex flex-col justify-end overflow-hidden">
                    <div
                      className="bg-neutral-300"
                      style={{ height: `${salePct}%` }}
                      title="Satış"
                    />
                    <div
                      className={`absolute left-0 right-0 bottom-0 ${
                        positive ? "bg-emerald-600" : "bg-rose-500"
                      } opacity-90`}
                      style={{ height: `${profitPct}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-neutral-500 uppercase tracking-widest">
                    {monthLabel(r.month)}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Alt satır: toplam satış vs maliyet */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-black/10 text-xs">
            <div className="flex items-center gap-4">
              <LegendDot color="bg-neutral-300" label={`Satış ${fmtTRY(totals.sale)}`} />
              <LegendDot color="bg-emerald-600" label={`Kâr ${fmtTRY(totals.profit)}`} />
              <span className="text-neutral-500 font-mono-num">Maliyet {fmtTRY(totals.cost)}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-neutral-600">
      <span className={`inline-block w-3 h-3 ${color}`} />{label}
    </span>
  );
}

// Kısa TL formatlaması (grafik üstü etiket için)
function fmtShort(n) {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}M ₺`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}k ₺`;
  return `${sign}${abs.toLocaleString("tr-TR", { maximumFractionDigits: 0 })} ₺`;
}
